import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query-client'
import * as pacientesApi from '@/lib/api/pacientes'
import type { PacienteIn } from '@/types/api'

export function usePacientes(search?: string) {
  return useQuery({
    queryKey: queryKeys.pacientes.list(search),
    queryFn: () => pacientesApi.list({ search }),
    staleTime: 30_000, // 30 segundos — reduce re-fetches innecesarios al cambiar foco
  })
}

export function usePaciente(dni: string) {
  return useQuery({
    queryKey: queryKeys.pacientes.detail(dni),
    queryFn: () => pacientesApi.get(dni),
    enabled: !!dni,
  })
}

export function useCreatePaciente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: PacienteIn) => pacientesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.pacientes.all })
    },
  })
}

export function useUpdatePaciente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dni, data }: { dni: string; data: PacienteIn }) =>
      pacientesApi.update(dni, data),
    onSuccess: (_result, { dni }) => {
      qc.invalidateQueries({ queryKey: queryKeys.pacientes.all })
      qc.invalidateQueries({ queryKey: queryKeys.pacientes.detail(dni) })
    },
  })
}

export function useDeletePaciente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dni: string) => pacientesApi.remove(dni),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.pacientes.all })
    },
  })
}

export function useUpdatePipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dni, pipeline }: { dni: string; pipeline: string }) =>
      pacientesApi.updatePipeline(dni, pipeline),
    onSuccess: (_result, { dni }) => {
      qc.invalidateQueries({ queryKey: queryKeys.pacientes.all })
      qc.invalidateQueries({ queryKey: queryKeys.pacientes.detail(dni) })
    },
  })
}
